/**
 * Student → Parents lookup (inverse of services/parent/students.ts)
 *
 * Resolves a student's linked parents via the `student_parents` junction:
 *   students.id → student_parents.student_id → parents.id → users (status)
 *
 * Two entry points:
 *   - useGetStudentParents(student_id) — for teacher/registrar/PT views
 *     where the caller already has a `students.id` (from the URL param).
 *   - useGetMyParents() — for a logged-in student's own profile page
 *     (resolves auth.profile.id → users.id → students.id → parents).
 *
 * Why a hook and not a join embedded on the existing useGetStudent call?
 * Because the four reader call-sites currently read three different
 * legacy shapes (`details.parents`, `parents_id` array, `student.parents`),
 * and threading the embed through every legacy hook would touch more code
 * than the dedicated hook does. The junction itself is the source of truth.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "@utils/helper";

export interface StudentParentRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  user_name: string | null;
  id_number: string | null;
  mobile_number: string | null;
  status: string | null;
}

async function fetchParentsForStudent(
  student_id: string,
): Promise<StudentParentRow[]> {
  // Embed parents → users to also surface the user-level status (active/
  // inactive). Disambiguate the parents→users FK — there are 3 (user_id,
  // created_by, updated_by). We want user_id.
  const { data, error } = await supabase
    .from("student_parents")
    .select(
      "parents(id, first_name, last_name, email, user_name, id_number, mobile_number, users!parents_user_id_fkey(status))",
    )
    .eq("student_id", student_id);
  if (error) throw error;
  return (data ?? [])
    // `parents` and the nested `users` are to-one embeds; PostgREST may type
    // each as an array, so normalize to the single related row.
    .map((row) => (Array.isArray(row.parents) ? row.parents[0] : row.parents))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .map((p) => {
      const user = Array.isArray(p.users) ? p.users[0] : p.users;
      return {
        id: p.id,
        first_name: p.first_name ?? null,
        last_name: p.last_name ?? null,
        email: p.email ?? null,
        user_name: p.user_name ?? null,
        id_number: p.id_number ?? null,
        mobile_number: p.mobile_number ?? null,
        status: user?.status ?? null,
      };
    });
}

export function useGetStudentParents(student_id: string | null | undefined) {
  const [rows, setRows] = useState<StudentParentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchParents = useCallback(async () => {
    if (!student_id) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchParentsForStudent(student_id));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load parents'));
    } finally {
      setLoading(false);
    }
  }, [student_id]);

  useEffect(() => {
    fetchParents();
  }, [fetchParents]);

  return { rows, loading, error, refetch: fetchParents };
}

export function useGetMyParents() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<StudentParentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchParents = useCallback(async () => {
    if (!profile?.id) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Resolve students.id from the auth profile (which is a users row).
      const { data: student, error: sErr } = await supabase
        .from("students")
        .select("id")
        .eq("user_id", profile.id)
        .maybeSingle();
      if (sErr) throw sErr;
      if (!student) {
        setRows([]);
        return;
      }
      setRows(await fetchParentsForStudent((student as { id: string }).id));
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load parents'));
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    fetchParents();
  }, [fetchParents]);

  return { rows, loading, error, refetch: fetchParents };
}
